import React, { createContext, useContext, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Box } from "@mui/material";
import ShepButton from "@/components/GenericComponents/ShepButton";

const ManageConnectionPopupContext = createContext(null);

export const ManageConnectionPopupProvider = ({ children }) => {

  // Footer portal target
  const [footerTarget, setFooterTargetState] = useState(null);
  const setFooterTarget = useCallback((node) => setFooterTargetState(node || null), []);

  // Subtab portal target
  const [subtabTarget, setSubtabTargetState] = useState(null);
  const setSubtabTarget = useCallback((node) => setSubtabTargetState(node || null), []);

  // Scroll management state for popup content
  const [scrollContainer, setScrollContainer] = useState(null);
  const [savedScrollTop, setSavedScrollTop] = useState(0);

  // Scroll utilities for popup content
  const registerScrollContainer = useCallback((el) => {
    setScrollContainer(el);
  }, []);

  const getScrollTop = useCallback(() => {
    return scrollContainer ? scrollContainer.scrollTop || 0 : 0;
  }, [scrollContainer]);

  const setScrollTop = useCallback(
    (top, behavior = "auto") => {
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: top || 0, left: 0, behavior });
      }
    },
    [scrollContainer]
  );

  const captureScroll = useCallback(() => {
    if (scrollContainer) setSavedScrollTop(scrollContainer.scrollTop || 0);
  }, [scrollContainer]);

  const restoreScroll = useCallback(
    (behavior = "auto") => {
      if (scrollContainer) setScrollTop(savedScrollTop, behavior);
    },
    [scrollContainer, savedScrollTop, setScrollTop]
  );

  const scrollToTop = useCallback(
    (behavior = "auto") => {
      setScrollTop(0, behavior);
    },
    [setScrollTop]
  );

  /* SHEP-776 No Longer Required 
  Enable/disable overflow on the main popup scroll container
  const setScrollEnabled = useCallback(
    (enabled) => {
      if (!scrollContainer) return;
      if (enabled) {
        // restore previous overflow if we have one, otherwise default to auto (y only)
        const prev = savedOverflow.current;
        if (prev !== undefined && prev !== null && prev !== "") {
          scrollContainer.style.overflow = prev;
        } else {
          scrollContainer.style.overflowX = "hidden";
          scrollContainer.style.overflowY = "scroll";
        }
      } else {
        // save current overflow and hide
        savedOverflow.current = scrollContainer.style.overflow;
        scrollContainer.style.overflow = "hidden";
      }
    },
    [scrollContainer]
  );
  */

  return (
    <ManageConnectionPopupContext.Provider
      value={{
        footerTarget,
        setFooterTarget,
        subtabTarget,
        setSubtabTarget,
        registerScrollContainer,
        getScrollTop,
        setScrollTop,
        captureScroll,
        restoreScroll,
        scrollToTop,
      }}
    >
      {children}
    </ManageConnectionPopupContext.Provider>
  );
};

export const useManageConnectionFooter = () => {
  const ctx = useContext(ManageConnectionPopupContext);
  if (!ctx)
    throw new Error(
      "useManageConnectionFooter must be used within a ManageConnectionPopupProvider"
    );
  return ctx;
};

export const FooterBarHost = ({ sx }) => {
  const { setFooterTarget } = useManageConnectionFooter();
  return (
    <Box
      className="popup-footer"
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        pt: 1,
        px: 3,
        flexShrink: 0,
        ...sx,
      }}
    >
      <div
        ref={setFooterTarget}
        style={{ display: "flex", gap: 12, padding: "4px 0", width: "100%", justifyContent: "flex-end" }}
      />
    </Box>
  );
};

export const FooterPortal = ({ children }) => {
  const { footerTarget } = useManageConnectionFooter();
  if (!footerTarget) return null;
  return createPortal(children, footerTarget);
};

export const FooterButton = ({ hidden = false, ...buttonProps }) => {
  const { footerTarget } = useManageConnectionFooter();
  if (hidden || !footerTarget) return null;
  return createPortal(<ShepButton {...buttonProps} />, footerTarget);
};

export const SubtabBarHost = ({ sx }) => {
  const { setSubtabTarget } = useManageConnectionFooter();
  return (
    <Box
      sx={{
        flexShrink: 0,
        backgroundColor: "var(--default-light)",
        ...sx,
      }}
    >
      <div ref={setSubtabTarget} />
    </Box>
  );
};

export const SubtabPortal = ({ children }) => {
  const { subtabTarget } = useManageConnectionFooter();
  if (!subtabTarget) return null;
  return createPortal(children, subtabTarget);
};